import styled, { createGlobalStyle, css } from 'styled-components';
import { Card, Table, Button as ButtonUI } from 'react-bootstrap';
import { COLORS } from 'modules/core/theme/colors';
import { STATIC_IMAGE_URLS } from 'modules/core/constants';
import { disabledSectionMixin } from 'modules/core/theme/mixins';
import { WindowBreakpoints } from 'modules/core/enums/windowBreakpoints.enum';

import { Title } from '../Title';

type SelectableButton = {
  selected: boolean;
};

export const TABLE_PADDING = '15px';

export const TableContainerWrapper = styled.div`
  margin-top: 20px;

  ${Title} {
    @media (max-width: ${WindowBreakpoints.small}px) {
      flex-direction: column;
      height: auto;
    }
  }
`;

export const TableHeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > div:nth-child(n) {
    padding-left: 10px;
  }

  & > div:nth-child(1) {
    @media (max-width: ${WindowBreakpoints.small}px) {
      padding-left: 0;
    }
  }

  & button,
  div {
    @media (max-width: ${WindowBreakpoints.small}px) {
      font-size: 14px;
      min-width: 100%;
    }
  }

  .title {
    font-size: ${({ theme: { fontSize } }) => fontSize.normal};
    font-weight: ${({ theme: { boldText } }) => boldText.bold};
  }

  @media (max-width: ${WindowBreakpoints.small}px) {
    justify-content: start;
    margin-top: 5px;
    flex-direction: column;
    align-items: start;

    & > div:nth-child(n) {
      margin-top: 10px;
      padding-left: 0;
    }
  }
`;

// No lock mixin here: Apply CPOV renders inside this wrapper and must stay clickable (SWA-8011).
export const TableTopSection = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: ${TABLE_PADDING};

  @media (max-width: ${WindowBreakpoints.medium}px) {
    flex-direction: column;
  }
`;

export const TableItems = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme: { colors } }) => colors['grey-3']};
  font-size: ${({ theme: { fontSize } }) => fontSize.normal};

  ${({ $disabled }) => $disabled && disabledSectionMixin}
`;

export const TotalItems = styled.span`
  margin-right: 10px;
`;

export const SelectAllSection = styled.div`
  display: flex;

  div {
    display: flex;
    align-items: center;
  }
`;

export const SelectAllLabel = styled.span`
  margin: 0 10px 0 5px;
`;

export const TableRow = styled.tr<{
  isRac?: boolean;
  isMakeItWork?: boolean;
  isMakeItWorkVehicle?: boolean;
  isRelative?: boolean;
  isBlur?: boolean;
  isLender?: boolean;
}>`
  ${({ isRac, isBlur, isLender }) =>
    (isRac || isLender) &&
    !isBlur &&
    `
      &:hover {
        background: ${COLORS.BLUE_2};
        td {
          border-right: 1px solid ${COLORS.BLUE_2} !important;
          border-left: 1px solid ${COLORS.WHITE} !important;
        }
      }
    `};

  position: ${({ isRelative }) => isRelative && 'relative'};

  ${({ isMakeItWork, isMakeItWorkVehicle }) =>
    isMakeItWork &&
    isMakeItWorkVehicle &&
    `
      &:hover .badge-ui {
        display: none;
      }

      &:hover .make-it-work-badge {
        display: initial;
      }
    `};

  ${({ isMakeItWorkVehicle }) =>
    isMakeItWorkVehicle &&
    `
      outline: 2px solid ${COLORS.GREEN_8};
      outline-offset: -3px;
    `};
`;

export const TableCell = styled.td<{
  isRac?: boolean;
  isSpecialColumn?: boolean;
  isBlur?: boolean;
  isLender?: boolean;
}>`
  ${({ isBlur }) =>
    isBlur &&
    `
      filter: blur(3px);
      background-color: rgba(0, 0, 0, 0.1) !important;
      user-select: none;
      pointer-events: none;
      --bs-table-accent-bg: unset !important;
    `};

  ${({ isRac, isSpecialColumn, isLender }) =>
    (isRac || isLender) &&
    `
    div div {
      padding: 0.5rem !important;
    }

    height: 100%;
    padding: ${isSpecialColumn ? `0.5rem` : `0`} !important;
  `}

  border: 1px solid ${({ theme: { colors } }) => colors.white};
`;

export const TableWrapper = styled(Table)<{ $isRac?: boolean; $isLender?: boolean; $isFilled: boolean }>`
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0 !important;

  &.table-bordered {
    tbody tr:first-child td {
      border-top: none;
    }

    td,
    th {
      border-left: 1px solid ${COLORS.WHITE_2};
      border-top: 1px solid ${COLORS.WHITE_2};
    }

    th:last-child,
    td:last-child {
      border-right: 1px solid ${COLORS.WHITE_2};
    }

    thead,
    th {
      background-color: ${COLORS.WHITE_2};

      /* Display the left border for all Table header cells except on the 1st and 2nd */
      &:nth-child(n + 3) {
        border-left: 0.5px solid ${({ theme: { colors } }) => colors['grey-30']};
      }
    }

    th {
      padding: 8px;
      vertical-align: top;
      white-space: break-spaces;
      ${({ $isFilled }) => $isFilled && `border-bottom: 1px solid ${COLORS.WHITE_2};`};
    }
  }
  ${({ $isRac, $isLender }) =>
    ($isRac || $isLender) &&
    `
    height: 100%;
    weight: 100%;
    tbody {
      tr td {
        height: 100%;
      }
    }
  `}
`;

const ToggleViewButton = styled.button<SelectableButton>`
  height: 36px;
  width: 36px;
  border: none;
  border-radius: 4px;
  margin-right: 10px;
  background-size: 18px 18px;
  background-repeat: no-repeat;
  background-position: center;
  background-color: ${(props) => (props.selected ? COLORS.GREY_1 : 'transparent')};

  &:hover {
    background-color: ${(props) => (props.selected ? COLORS.GREY_1 : COLORS.GREY_4)};
  }
`;

export const ToggleTableViewButton = styled(ToggleViewButton)`
  background-image: url(${STATIC_IMAGE_URLS.icons.table});

  @media (max-width: ${WindowBreakpoints.small}px) {
    display: none;
  }
`;

export const ToggleCardViewButton = styled(ToggleViewButton)`
  background-image: url(${STATIC_IMAGE_URLS.icons.cards});

  @media (max-width: ${WindowBreakpoints.small}px) {
    display: none;
  }
`;

export const OverflowTableWrapper = styled.div`
  overflow: auto;
  max-height: calc(100vh - 190px);
  border-bottom: 1px solid ${COLORS.WHITE_2};
`;

export const NoVehicleErrorMessage = styled(Card.Body)`
  margin: 0 0 10px 0;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
  color: ${COLORS.RED_6};
`;

export const UpgradeIcon = styled.span`
  display: inline-block;
  background-repeat: no-repeat;
  background-size: contain;
  height: 15px;
  width: 15px;
  background-image: url(${STATIC_IMAGE_URLS.icons.externalLink});
  vertical-align: middle;
  margin-right: 10px;
  transition: transform 0.2s ease-in-out;
  margin-bottom: 5px;
`;

export const UnlockButton = styled(ButtonUI)`
  height: 43px;
  width: 217px;
  font-size: 17px;
  white-space: nowrap;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translateY(42px);
  z-index: 1;
  color: ${({ theme: { colors } }) => colors['blue-5']} !important;
  background: #ffffff 0 0 no-repeat padding-box !important;
  border: 1px solid ${({ theme: { colors } }) => colors['blue-5']} !important;
  box-shadow: none !important;
  background-image: url(${STATIC_IMAGE_URLS.icons.print});
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;

  &:active,
  &:focus,
  &:hover {
    background: #ffffff 0 0 no-repeat padding-box !important;
    color: ${({ theme: { colors } }) => colors['blue-5']};
  }

  &:active,
  &:hover {
    ${UpgradeIcon} {
      transform: translateX(1px) translateY(-1px);
    }
  }
`;

export const UnlockOverlayCell = styled.td`
  position: relative;
  padding: 0 !important;
  border: none !important;
`;

export const AddInventoryBannerCell = styled.td<{ $disabled?: boolean }>`
  background: linear-gradient(to bottom, #e5f7fc, #ffffff) !important;
  padding: 7px 5px !important;
  border: none !important;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  user-select: none;
`;

export const AddInventoryBannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${COLORS.BLUE_5};
  font-size: 15px;
  font-weight: 600;

  img {
    height: 20px;
    filter: brightness(0) invert(1);
  }
`;

export const DemoButtonGlobalStyles = createGlobalStyle`
  @property --border-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }

  @keyframes spin-border {
    to { --border-angle: 360deg; }
  }
`;

const demoStyles = css`
  position: relative;
  height: 32px;
  padding: 0 20px;
  border-radius: 9999px;
  background: ${COLORS.WHITE};
  font-weight: 600;
  font-size: 14px;
  color: #0e2756;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1), color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 2px;
    border-radius: inherit;
    background: linear-gradient(90deg, #3dd3f3, #0561d9, #5dd7f6, #0561d9, #3dd3f3);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box exclude, linear-gradient(#fff 0 0);
    pointer-events: none;
  }

  @supports (background: conic-gradient(from 0deg, red, blue)) {
    &::before {
      background: conic-gradient(from var(--border-angle), #3dd3f3, #0561d9, #5dd7f6, #0561d9, #3dd3f3);
      animation: spin-border 3s linear infinite;
    }

    &:hover::before {
      animation-duration: 1.5s;
    }
  }

  &:hover {
    background-color: #0e2756;
    color: ${COLORS.WHITE};
    scale: 1.05;
    transition: scale 0.15s ease-in-out 0.1s;
  }
`;

export const DemoTagSimple = styled.span`
  font-size: 13px;
  padding: 0px 10px;
  background-color: ${COLORS.WHITE};
  color: ${COLORS.BLUE_5};
  border: 1px solid ${COLORS.BLUE_5};
  &:hover {
    background-color: #0e2756;
    color: ${COLORS.WHITE};
    scale: 1.05;
    transition: scale 0.15s ease-in-out 0.1s;
  }
  border-radius: 9999px;
`;

export const DemoButton = styled.button`
  ${demoStyles}
  border: none;
  cursor: pointer;
  font-size: 12px;
  margin-top: 10px;
  margin-bottom: 10px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
